import { Link, useLocation } from 'react-router-dom'
import type { Lang } from '@/hooks/useLang'

interface HeaderProps {
  lang: Lang
  audioEnabled: boolean
  audioPlaying: boolean
  onToggleAudio: () => void
  onToggleLang: () => void
}

const NAV_LINKS: Record<Lang, { label: string; href: string }[]> = {
  fr: [
    { label: 'PROTOCOLES', href: '/#protocols' },
    { label: 'SPÉCIFICATIONS', href: '/#features' },
    { label: 'GALERIE', href: '/#preview' },
  ],
  en: [
    { label: 'PROTOCOLS', href: '/#protocols' },
    { label: 'SPECIFICATIONS', href: '/#features' },
    { label: 'GALLERY', href: '/#preview' },
  ],
}

export default function Header({ lang, audioEnabled, audioPlaying, onToggleAudio, onToggleLang }: HeaderProps) {
  const { pathname } = useLocation()

  return (
    <header className="fixed top-0 w-full flex justify-between items-center px-8 h-20 bg-slate-950/80 backdrop-blur-xl z-50 shadow-[0_0_40px_rgba(0,240,255,0.05)]">
      {/* Logo */}
      <Link to="/" className="flex items-center gap-3 group">
        <span className="material-symbols-outlined text-primary-container text-2xl select-none">
          memory
        </span>
        <span className="text-xl font-bold tracking-tighter text-primary-container font-headline uppercase group-hover:text-primary transition-colors duration-300">
          ROBOT.FRIC.LINK
        </span>
      </Link>

      {/* Nav */}
      <nav className="hidden md:flex gap-8">
        {NAV_LINKS[lang].map(({ label, href }) => {
          const isActive = pathname === '/' && href.startsWith('/#')
          return (
            <a
              key={label}
              href={href}
              className={[
                'font-headline tracking-widest uppercase text-sm transition-all duration-200',
                isActive
                  ? 'text-primary-container border-b-2 border-primary-container'
                  : 'text-on-surface-variant hover:text-primary transition-colors',
              ].join(' ')}
            >
              {label}
            </a>
          )
        })}
      </nav>

      {/* Actions */}
      <div className="flex items-center gap-6">
        {/* Audio toggle */}
        <button
          onClick={onToggleAudio}
          className="flex items-center gap-2 text-on-surface-variant hover:text-primary transition-colors font-body text-xs tracking-widest"
          aria-label={audioEnabled ? 'Désactiver le son' : 'Activer le son'}
          title={audioEnabled ? 'Désactiver la narration' : 'Activer la narration'}
        >
          <span className="material-symbols-outlined text-lg">
            {audioEnabled ? (audioPlaying ? 'volume_up' : 'volume_down') : 'volume_off'}
          </span>
          <span className="hidden sm:inline">
            {lang === 'fr'
              ? audioEnabled ? 'SON: ON' : 'SON: OFF'
              : audioEnabled ? 'AUDIO: ON' : 'AUDIO: OFF'
            }
          </span>
        </button>

        {/* Lang toggle */}
        <button
          onClick={onToggleLang}
          className="font-headline tracking-widest uppercase text-sm text-primary-container ghost-border px-4 py-1.5 hover:bg-surface-container-high/40 transition-all duration-300 active:scale-95 rounded-lg"
          aria-label="Changer de langue"
        >
          {lang === 'fr' ? 'EN' : 'FR'}
        </button>
      </div>
    </header>
  )
}
